"use client";

import Image from "next/image";
import { ArrowRight, Check, Menu, Share2 } from "lucide-react";
import { useTranslations } from "next-intl";
import React, { useEffect, useMemo, useRef, useState } from "react";
import hisaniLogo from "../../../../../../../brand/hisani_logo.png";
import { Link } from "../../../../i18n/navigation";
import styles from "../../../[locale]/stopwatch/stopwatch-story.module.css";

type StopwatchStoryStep = "timing" | "splits" | "timers" | "finish";

const STORY_STEPS: readonly StopwatchStoryStep[] = [
  "timing",
  "splits",
  "timers",
  "finish",
];

export function StopwatchExplainerStory() {
  const t = useTranslations("Stopwatch");
  const tExplainer = useTranslations("ProductExplainer.stopwatch");
  const [activeStep, setActiveStep] = useState<StopwatchStoryStep>("timing");
  const storyStepElements = useRef(new Map<StopwatchStoryStep, HTMLElement>());

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      return;
    }

    const visibleSteps = new Map<StopwatchStoryStep, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const step = (entry.target as HTMLElement).dataset[
            "stopwatchStoryStep"
          ] as StopwatchStoryStep | undefined;
          if (step && STORY_STEPS.includes(step)) {
            if (entry.isIntersecting) {
              visibleSteps.set(step, entry.intersectionRatio);
            } else {
              visibleSteps.delete(step);
            }
          }
        }

        const nextStep = [...visibleSteps.entries()].sort(
          ([, firstRatio], [, secondRatio]) => secondRatio - firstRatio,
        )[0]?.[0];
        if (nextStep !== undefined) {
          setActiveStep(nextStep);
        }
      },
      {
        rootMargin: "-18% 0px -48% 0px",
        threshold: [0, 0.25, 0.5, 0.75],
      },
    );

    for (const element of storyStepElements.current.values()) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, []);

  const narrative = useMemo(
    () => (
      <section
        className={styles["narrative"]}
        aria-label={t("story.regionLabel")}
      >
        <ol className={styles["stepList"]}>
          {STORY_STEPS.map((key, index) => {
            const isActive = key === activeStep;
            return (
              <li
                key={key}
                ref={(element) => {
                  if (element === null) {
                    storyStepElements.current.delete(key);
                  } else {
                    storyStepElements.current.set(key, element);
                  }
                }}
                className={`${styles["step"]} ${isActive ? styles["activeStep"] : ""}`}
                data-stopwatch-story-step={key}
                aria-current={isActive ? "step" : undefined}
              >
                <div className={styles["stepContent"]}>
                  <p className={styles["stepProgress"]}>
                    {t("story.progress", {
                      step: index + 1,
                      total: STORY_STEPS.length,
                    })}
                  </p>
                  <p className={styles["stepEyebrow"]}>
                    {t(`story.steps.${key}.eyebrow`)}
                  </p>
                  <h2 className={styles["stepTitle"]}>
                    {t(`story.steps.${key}.title`)}
                  </h2>
                  <p className={styles["stepDescription"]}>
                    {t(`story.steps.${key}.description`)}
                  </p>
                  <p className={styles["stepDetail"]}>
                    {t(`story.steps.${key}.detail`)}
                  </p>

                  {/* Prominent CTA on each chapter */}
                  <div className="mt-8 flex flex-wrap items-center gap-3">
                    <Link
                      href="/stopwatch"
                      className="inline-flex min-h-11 items-center gap-2 rounded-md bg-brand px-5 text-sm font-bold text-on-brand shadow-elevation-1 transition-colors hover:bg-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                    >
                      <span>{tExplainer("launchAction")}</span>
                      <ArrowRight
                        className="size-4 rtl:-scale-x-100"
                        aria-hidden="true"
                      />
                    </Link>
                    <Link
                      href="/stopwatch"
                      className="inline-flex min-h-11 items-center gap-2 rounded-md border border-border-default bg-surface px-4 text-xs font-semibold text-primary transition-colors hover:bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                    >
                      {tExplainer("ctaDesc")}
                    </Link>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </section>
    ),
    [activeStep, t, tExplainer],
  );

  return (
    <div className={styles["story"]}>
      {narrative}

      {/* Phone Mockup Preview */}
      <div className={styles["deviceColumn"]}>
        <div className={styles["phone"]} data-stopwatch-phone>
          <span className={styles["sideButtonTop"]} aria-hidden="true" />
          <span className={styles["sideButtonMiddle"]} aria-hidden="true" />
          <span className={styles["sideButtonBottom"]} aria-hidden="true" />
          <div className={styles["phoneScreen"]} data-stopwatch-phone-screen>
            <span
              className={styles["dynamicIsland"]}
              data-stopwatch-dynamic-island
              aria-hidden="true"
            />
            <div className={styles["phoneChrome"]} data-stopwatch-phone-header>
              <Image
                src={hisaniLogo}
                alt=""
                width={153}
                height={75}
                className={styles["phoneLogo"]}
              />
              <span
                className={styles["phoneMenu"]}
                data-stopwatch-phone-menu
                aria-hidden="true"
              >
                <Menu strokeWidth={2.25} />
              </span>
            </div>

            <div className={styles["phoneViewport"]}>
              <div className="p-4 bg-canvas min-h-full">
                {/* Sample Timer Card 1 */}
                <div className="rounded-xl border border-border-subtle bg-surface p-4 shadow-elevation-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-primary">
                      Horse 1
                    </span>
                    <span className="rounded-xs bg-brand-subtle px-2 py-0.5 text-xs font-bold text-brand">
                      {activeStep === "finish" ? "Finished" : "Running"}
                    </span>
                  </div>

                  <div className="my-4 text-center">
                    <span className="font-mono text-4xl font-extrabold tracking-tight text-primary">
                      {activeStep === "timing"
                        ? "00:48.32"
                        : activeStep === "splits"
                          ? "01:12.85"
                          : activeStep === "timers"
                            ? "01:36.40"
                            : "01:36.40"}
                    </span>
                  </div>

                  {/* Sample Sectional Splits */}
                  {(activeStep === "splits" ||
                    activeStep === "timers" ||
                    activeStep === "finish") && (
                    <div className="mt-3 border-t border-border-subtle pt-3 text-xs">
                      <div className="flex justify-between py-1 text-secondary">
                        <span>400m (Furlong 2)</span>
                        <span className="font-mono font-semibold text-primary">
                          00:24.18
                        </span>
                        <span className="font-mono text-secondary">
                          59.5 km/h
                        </span>
                      </div>
                      <div className="flex justify-between py-1 text-secondary">
                        <span>800m (Furlong 4)</span>
                        <span className="font-mono font-semibold text-primary">
                          00:48.32
                        </span>
                        <span className="font-mono text-secondary">
                          60.2 km/h
                        </span>
                      </div>
                      {activeStep === "finish" && (
                        <div className="flex justify-between py-1 text-secondary">
                          <span>1200m (Finish)</span>
                          <span className="font-mono font-semibold text-primary">
                            01:12.85
                          </span>
                          <span className="font-mono text-secondary">
                            58.9 km/h
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Second timer for "timers" / "finish" step */}
                {(activeStep === "timers" || activeStep === "finish") && (
                  <div className="mt-4 rounded-xl border border-border-subtle bg-surface p-4 shadow-elevation-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-primary">
                        Horse 2
                      </span>
                      <span className="rounded-xs bg-subtle px-2 py-0.5 text-xs font-bold text-secondary">
                        {activeStep === "finish" ? "Finished" : "Running"}
                      </span>
                    </div>
                    <div className="my-3 text-center">
                      <span className="font-mono text-3xl font-bold tracking-tight text-primary">
                        01:37.12
                      </span>
                    </div>
                  </div>
                )}

                {/* Action buttons preview */}
                <div className="mt-6 flex flex-col gap-2">
                  <div className="flex gap-2">
                    <div className="flex-1 flex min-h-11 items-center justify-center rounded-md bg-brand text-xs font-bold text-on-brand">
                      <Check className="me-1.5 size-4" />
                      Save Session
                    </div>
                    <div className="flex min-h-11 items-center justify-center rounded-md border border-border-default bg-surface px-4 text-xs font-semibold text-primary">
                      <Share2 className="size-4" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <span
              className={styles["homeIndicator"]}
              data-stopwatch-home-indicator
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
